import React, { useCallback, useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../../../utils/apiFetch';
import apiPath from '../../../utils/pathObj';
import useToastContext from 'hooks/useToastContext';
import { Btn, Card, Item, StatusBadge, Flag, fmtDate, textareaCls, BORDER, errMsg } from './adminUi';
import AdminEvereeCredentials from './AdminEvereeCredentials';
import {
  ConfirmModal, RejectModal, RequestInfoModal, PaymentModal, SendEvereeModal,
} from './AdminActionModals';

const DocLink = ({ label, url }) => (
  <li className="flex items-center justify-between gap-3 border-b border-gray-100 py-2 last:border-b-0">
    <span className="text-sm text-gray-800">{label}</span>
    {url ? (
      <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-blue-700 hover:underline">View ↗</a>
    ) : (
      <span className="text-xs font-semibold text-red-600">Missing</span>
    )}
  </li>
);

const AdminPayrollReview = ({ id, onBack }) => {
  const notification = useToastContext();
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null); // 'approve' | 'reject' | 'info' | 'payment' | 'send' | 'reset'
  const [note, setNote] = useState('');

  const base = `${apiPath.payrollAdminBase}`;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiGet(`${base}/${id}`);
      if (res?.data?.success) setRec(res.data.results);
      else notification.error(errMsg(res, 'Could not load record'));
    } catch (e) {
      notification.error('Could not load record');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { load(); }, [load]);

  /** run an admin action, refresh the record from the response */
  const run = async (call, successMsg) => {
    try {
      setBusy(true);
      const res = await call();
      if (res?.data?.success) {
        setRec(res.data.results);
        notification.success(successMsg);
        setModal(null);
        return true;
      }
      notification.error(errMsg(res, 'Action failed'));
    } catch (e) {
      notification.error('Action failed');
    } finally {
      setBusy(false);
    }
    return false;
  };

  const approve = () => run(() => apiPut(`${base}/${id}/approve`, {}), 'Onboarding approved');
  const reject = (b) => run(() => apiPut(`${base}/${id}/reject`, b), 'Onboarding rejected');
  const requestInfo = (b) => run(() => apiPut(`${base}/${id}/request-info`, b), 'Information requested from client');
  const recordPayment = (b) => run(() => apiPut(`${base}/${id}/payment`, b), 'Payment recorded');
  // TESTING ONLY – remove with the backend test-reset route
  const resetData = async () => {
    try {
      setBusy(true);
      const res = await apiPost(`${base}/test-reset/${rec.companyID?._id || rec.companyID}`, {});
      if (res?.data?.success) {
        notification.success('Payroll data removed for this company');
        onBack();
        return;
      }
      notification.error(errMsg(res, 'Reset failed'));
    } catch (e) {
      notification.error('Reset failed');
    } finally {
      setBusy(false);
    }
  };
  const send = () => run(() => apiPut(`${base}/send-to-everee/${id}`, {}), 'Package emailed to Everee');
  const addNote = async () => {
    if (!note.trim()) return;
    if (await run(() => apiPost(`${base}/${id}/notes`, { note }), 'Note added')) setNote('');
  };

  if (loading && !rec) return <p className="py-20 text-center text-gray-400">Loading…</p>;
  if (!rec) return <div className="py-20 text-center"><Btn onClick={onBack}>← Back to list</Btn></div>;

  const { companyInfo: c, payrollInfo: p, agreement: a, documents: d, payment, readiness: r } = rec;
  const pc = c.primaryContact || {};
  const s = rec.status;
  const openInfo = (rec.infoRequests || []).filter((i) => !i.resolvedAt);

  return (
    <div className="mx-auto max-w-6xl">
      <button type="button" onClick={onBack} className="mb-4 text-sm font-semibold text-blue-700 hover:underline">← All onboardings</button>

      {/* header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{c.legalName}</h1>
          <p className="mt-1 text-sm text-gray-500">EIN {c.ein} · Submitted {fmtDate(rec.createdAt)}</p>
          {rec.companyID && (
            <p className="mt-0.5 text-sm text-gray-500">
              Company account: <span className="font-semibold text-gray-700">{rec.companyID.name}</span>
              {rec.companyID.officialEmail ? ` · ${rec.companyID.officialEmail}` : ''}
            </p>
          )}
        </div>
        <StatusBadge status={s} className="!px-3 !py-1 !text-sm" />
      </div>

      {s === 'Rejected' && rec.rejection && (
        <div className="mb-6 rounded border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <b>Rejected</b> by {rec.rejection.byName} on {fmtDate(rec.rejection.at)} — {rec.rejection.reason}
        </div>
      )}
      {s === 'Pending Empivo Review' && rec.lastSendError && (
        <div className="mb-6 rounded border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <b>Automatic email to Everee failed:</b> {rec.lastSendError} — use “Preview &amp; Send to Everee” to retry.
        </div>
      )}
      {s === 'Information Requested' && openInfo.length > 0 && (
        <div className="mb-6 rounded border border-orange-200 bg-orange-50 p-4 text-sm text-orange-900">
          <b>Waiting on client:</b> {openInfo[openInfo.length - 1].message}
          {openInfo[openInfo.length - 1].items?.length > 0 && <> ({openInfo[openInfo.length - 1].items.join(', ')})</>}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* ---------------- left: submitted data ---------------- */}
        <div className="space-y-6 lg:col-span-2">
          <Card title="Company">
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Item label="Legal name">{c.legalName}</Item>
              <Item label="Trade name">{c.dbaName}</Item>
              <Item label="Address">{[c.address, c.city, c.state, c.zip].filter(Boolean).join(', ')}</Item>
              <Item label="Phone">{c.phone}</Item>
              <Item label="Website">{c.website}</Item>
              <Item label="Nature of business">{c.natureOfBusiness}</Item>
              <Item label="NAICS">{c.naicsCode}</Item>
              <Item label="Annual revenue">{p.estimatedAnnualRevenue != null ? `$${Number(p.estimatedAnnualRevenue).toLocaleString()}` : ''}</Item>
              <Item label="Primary contact">{[pc.name, pc.title && `(${pc.title})`].filter(Boolean).join(' ')}</Item>
              <Item label="Contact email / phone">{[pc.email, pc.phone].filter(Boolean).join(' · ')}</Item>
            </dl>
          </Card>

          <Card title="Owners & control person">
            <div className="space-y-3">
              {rec.owners.map((o, i) => (
                <div key={i} className={`border ${BORDER} p-3`}>
                  <div className="mb-2 flex items-center justify-between">
                    <p className="font-semibold text-gray-900">{o.fullName}</p>
                    <span className="flex gap-2">
                      <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700">{o.ownershipPercentage}%</span>
                      {o.isControlPerson && <span className="rounded bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800">Control Person</span>}
                    </span>
                  </div>
                  <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    <Item label="SSN">{o.ssnLast4 ? `•••-••-${o.ssnLast4}` : ''}</Item>
                    <Item label="DOB">{o.dob ? new Date(o.dob).toLocaleDateString(undefined, { timeZone: 'UTC' }) : ''}</Item>
                    <Item label="Phone">{o.phone}</Item>
                    <Item label="Email">{o.email}</Item>
                    <div className="col-span-2"><Item label="Address">{[o.homeAddress, o.city, o.state, o.zip].filter(Boolean).join(', ')}</Item></div>
                  </dl>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Payroll information">
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <Item label="Worker types">{p.payType === 'Both' ? 'W-2 & 1099' : p.payType}</Item>
              <Item label="W-2 employees">{p.w2EmployeeCount}</Item>
              <Item label="1099 contractors">{p.contractorCount}</Item>
              <Item label="Pay period">{p.payFrequency}</Item>
              <Item label="Starting day">{p.payPeriodStartingDay}</Item>
              <Item label="States">{(p.states || []).join(', ')}</Item>
            </dl>
          </Card>

          <Card title="Documents">
            <ul>
              {(d.ownerIdUrls || []).length === 0 && <DocLink label="Owner ID" />}
              {(d.ownerIdUrls || []).map((u, i) => <DocLink key={u} label={`Owner ID ${i + 1}`} url={u} />)}
              <DocLink label="IRS EIN letter" url={d.einLetterUrl} />
              <DocLink label="Signed client contract" url={d.signedContractUrl} />
              <DocLink label="Signed Empivo/Everee agreement (PDF)" url={a.completedPdfUrl} />
              {(d.additionalDocsUrl || []).map((u, i) => <DocLink key={u} label={`Additional document ${i + 1}`} url={u} />)}
            </ul>
          </Card>

          <Card title="Agreement & ACH authorization">
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <Item label="Agreement"><Flag ok={a.isSigned} yes="Signed" no="Not signed" /></Item>
              <Item label="Signer">{a.signerName}{a.signerTitle ? `, ${a.signerTitle}` : ''}</Item>
              <Item label="Signed at">{fmtDate(a.signedAt)}</Item>
              <Item label="Institution">{a.bankDetails?.institutionName}</Item>
              <Item label="Routing">{a.bankDetails?.routingLast4 ? `•••••${a.bankDetails.routingLast4}` : ''}</Item>
              <Item label="Account">{a.bankDetails?.accountLast4 ? `••••${a.bankDetails.accountLast4}` : ''}</Item>
              <Item label="Version">{a.agreementVersion}</Item>
              <Item label="Initials">{a.subscription?.initials}</Item>
            </dl>
          </Card>

          <Card title="Activity">
            <ol className="space-y-3 border-l-2 border-gray-200 pl-4">
              {[...(rec.statusHistory || [])].reverse().map((h, i) => (
                <li key={i} className="relative text-sm">
                  <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-blue-600 ring-2 ring-white" />
                  <p className="font-semibold text-gray-900">{h.status}</p>
                  {h.note && <p className="text-gray-600">{h.note}</p>}
                  <p className="text-[11px] text-gray-500">{h.byName} · {fmtDate(h.at)}</p>
                </li>
              ))}
              <li className="relative text-sm">
                <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-gray-400 ring-2 ring-white" />
                <p className="font-semibold text-gray-900">Submitted by client</p>
                <p className="text-[11px] text-gray-500">{fmtDate(rec.createdAt)}</p>
              </li>
            </ol>
          </Card>
        </div>

        {/* ---------------- right: review & actions ---------------- */}
        <div className="space-y-6">
          <AdminEvereeCredentials rec={rec} id={id} onUpdated={setRec} />

          <Card title="Actions">
            <div className="space-y-2">
              {(s === 'Pending Empivo Review' || s === 'Information Requested') && (
                <Btn variant="primary" className="w-full" onClick={() => setModal('approve')}>Approve</Btn>
              )}
              {['Pending Empivo Review', 'Approved', 'Submitted to Everee'].includes(s) && (
                <Btn className="w-full" onClick={() => setModal('info')}>Request information</Btn>
              )}
              {(s === 'Pending Empivo Review' || s === 'Approved') && (
                <Btn variant="success" className="w-full" onClick={() => setModal('send')}>Preview &amp; Send to Everee</Btn>
              )}
              {s === 'Submitted to Everee' && (
                <>
                  <Btn className="w-full" onClick={() => setModal('send')}>Resend to Everee</Btn>
                </>
              )}
              {['Pending Empivo Review', 'Information Requested', 'Approved', 'Submitted to Everee', 'Active'].includes(s) && (
                <Btn variant="danger" className="w-full" onClick={() => setModal('reject')}>Reject</Btn>
              )}
              {s === 'Rejected' && <p className="text-sm text-gray-500">No further actions available.</p>}
            </div>
            {rec.submissionLog?.sentAt && (
              <p className="mt-3 border-t border-gray-100 pt-3 text-xs text-gray-500">
                Last sent {fmtDate(rec.submissionLog.sentAt)} to {rec.submissionLog.recipientEmail}
              </p>
            )}
          </Card>

          <Card title="Ready for Everee?">
            <ul className="space-y-2 text-sm">
              <li className="flex justify-between">Payroll Setup <Flag ok={r.payrollSetup} yes="Complete" no="Incomplete" /></li>
              <li className="flex justify-between">Agreement <Flag ok={r.agreementSigned} yes="Signed" no="Unsigned" /></li>
              <li className="flex justify-between">Payment <Flag ok={r.paymentPaid} yes="Paid" no="Unpaid" /></li>
              <li className="flex justify-between">Required Documents <Flag ok={r.requiredDocuments} yes="Complete" no="Missing" /></li>
              <li className="flex justify-between border-t border-gray-200 pt-2 font-bold">Ready for Everee <Flag ok={r.ready} /></li>
            </ul>
            {payment?.isPaid && (
              <p className="mt-3 text-xs text-gray-500">
                Paid ${payment.amountPaid} by <b className="text-gray-700">{rec.companyID?.name || c.legalName}</b>
                {payment.paidAt ? ` on ${fmtDate(payment.paidAt)}` : ''}
                {payment.transactionId ? ` · ${payment.transactionId}` : ''}
              </p>
            )}
            {!payment?.isPaid && s !== 'Rejected' && (
              <Btn className="mt-3 w-full" onClick={() => setModal('payment')}>Record payment received</Btn>
            )}
          </Card>

          <Card title="Internal notes">
            <textarea rows={3} className={textareaCls} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note (not visible to the client)…" />
            <Btn className="mt-2" disabled={busy || !note.trim()} onClick={addNote}>Add note</Btn>
            <ul className="mt-4 space-y-3">
              {[...(rec.adminNotes || [])].reverse().map((n, i) => (
                <li key={i} className="rounded bg-[#faf9f7] p-2.5 text-sm text-gray-800">
                  {n.note}
                  <p className="mt-1 text-[11px] text-gray-500">{n.byName} · {fmtDate(n.at)}</p>
                </li>
              ))}
            </ul>
          </Card>

          {/* TESTING ONLY – remove this block (and the 'reset' modal below) after testing */}
          {/* <div className="rounded border border-dashed border-red-300 bg-red-50/50 p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-red-700">Testing only</p>
            <p className="mt-1 text-xs text-gray-600">Deletes this company’s payroll payment, form and Everee credentials so you can start over.</p>
            <Btn className="mt-3 w-full !border-red-300 !text-red-700 hover:!bg-red-50" onClick={() => setModal('reset')}>Remove payroll data</Btn>
          </div> */}
        </div>
      </div>

      {/* ---------------- modals ---------------- */}
      {modal === 'approve' && (
        <ConfirmModal title="Approve onboarding" body={`Approve ${c.legalName}? It can then be sent to Everee.`} confirmLabel="Approve" busy={busy} onConfirm={approve} onClose={() => setModal(null)} />
      )}
      {modal === 'reject' && <RejectModal busy={busy} onSubmit={reject} onClose={() => setModal(null)} />}
      {modal === 'info' && <RequestInfoModal busy={busy} onSubmit={requestInfo} onClose={() => setModal(null)} />}
      {modal === 'payment' && <PaymentModal busy={busy} onSubmit={recordPayment} onClose={() => setModal(null)} />}
      {modal === 'reset' && (
        <ConfirmModal
          title="Remove payroll data (testing only)"
          body={`This permanently deletes the payment(s), onboarding form and Everee credentials of ${rec.companyID?.name || c.legalName}. They will see the payment screen again. Uploaded files and Stripe records are not touched.`}
          confirmLabel="Delete payroll data"
          variant="danger"
          busy={busy}
          onConfirm={resetData}
          onClose={() => setModal(null)}
        />
      )}
      {modal === 'send' && <SendEvereeModal id={id} resend={s === 'Submitted to Everee'} busy={busy} onSend={send} onClose={() => setModal(null)} />}
    </div>
  );
};

export default AdminPayrollReview;